import ApiService from '@/common/api.service'
import clientNotesModule from './clientNotesModule'
import clientContactsModule from './clientContactsModule'
import clientAttachmentsModule from './clientAttachmentsModel'
import clientLocationsModule from './clientLocationsModule'

import * as utils from '@/common/utils'
import * as notesHelpers from '@/store/notes/notes.helpers'
import * as contactsHelpers from '@/store/contacts/contacts.helpers'
import * as attachmentsHelpers from '@/store/attachments/attachments.helpers'
import * as locationsHelpers from '@/store/locations/locations.helpers'
import store from '@/store'

const module = 'admin/clientsModule'
const notesModule = module + '/notes'
const contactsModule = module + '/contacts'
const attachmentsModule = module + '/attachments'
const locationsModule = module + '/locations'

/**
 * Returns a copy of the default state.
 *
 * @return Object
 */
function defaults () {
  return {
    id: null,
    name: '',
    fin_account_no: '',
    description: '',
    partner: {
      id: null,
      name: ''
    },
    agency: {
      id: null,
      name: ''
    },
    created_at: '',
    updated_at: '',
    note: '',
    transferDetails: []
  }
}

function formDefaults () {
  return {
    partners: []
  }
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param client      Object
 * @param notes       Array|null
 * @param contacts    Array|null
 * @param attachments Array|null
 * @param locations   Array|null
 *
 * @return Object
 */
function transformCreate (client, notes = null, contacts = null, attachments = null, locations = null) {
  return {
    id: client.id,
    name: client.name,
    fin_account_no: client.fin_account_no,
    description: client.description,
    partner_id: client.partner.id,
    agency_id: client.agency.id,
    notes,
    contacts,
    attachments,
    locations
  }
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param client  Object
 *
 * @return Object
 */
function transformUpdate (client) {
  return {
    id: client.id,
    name: client.name,
    fin_account_no: client.fin_account_no,
    description: client.description
  }
}

/**
 * The module's state (VueX store object)
 */
export const state = {
  data: Object.assign({}, defaults()),
  form: Object.assign({}, formDefaults()),
  original: {
    name: '',
    fin_account_no: '',
    description: ''
  },
  isModified: false
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @return Object
   */
  get (state) {
    return state.data
  },

  /**
   * Returns the form's state object
   *
   * @return Object
   */
  getForm (state) {
    return state.form
  },

  /**
   * Returns true if the primary data have been modified
   * since it was originally loaded.
   *
   * @return Boolean
   */
  isModified (state) {
    return (state.data.name !== state.original.name) ||
      ((state.data.fin_account_no || null) !== state.original.fin_account_no) ||
      ((state.data.description || null) !== state.original.description)
  }
}

export const actions = {
  /**
   * Request a record from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetch (context, id) {
    return ApiService
      .get(`clients/${id}`)
      .then((response) => {
        context.commit('set', response.data.data)
        notesHelpers.setNotes(notesModule, response.data.data.notes)
        contactsHelpers.setContacts(contactsModule, response.data.data.contacts)
        contactsHelpers.setContactTypes(contactsModule, response.data.data.contact_types)
        attachmentsHelpers.setAttachments(attachmentsModule, response.data.data.attachments)
        locationsHelpers.setLocations(locationsModule, response.data.data.locations)
        return response
      })
  },

  /**
   * Request "create" form data from the API.
   *
   * @param context Object
   *
   * @return Promise
   */
  fetchFormCreate (context) {
    return ApiService
      .get(`clients/create`)
      .then((response) => {
        context.commit('set', defaults())
        context.commit('setFormData', response.data.data)
        notesHelpers.purge(notesModule)
        contactsHelpers.purge(contactsModule)
        contactsHelpers.setContactTypes(contactsModule, response.data.data.contact_types)
        attachmentsHelpers.purge(attachmentsModule)
        locationsHelpers.purge(locationsModule)
        locationsHelpers.setLocationTypes(locationsModule, response.data.data.location_types)
        return response
      })
  },

  /**
   * Request a record and "edit" form data from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetchFormEdit (context, id) {
    return ApiService
      .get(`clients/${id}/edit/`)
      .then((response) => {
        context.commit('set', response.data.data)
        context.commit('setFormData', response.data.data)
        context.commit('setOriginal', response.data.data)
        notesHelpers.setNotes(notesModule, response.data.data.notes)
        contactsHelpers.setContacts(contactsModule, response.data.data.contacts)
        contactsHelpers.setContactTypes(contactsModule, response.data.data.contact_types)
        attachmentsHelpers.setAttachments(attachmentsModule, response.data.data.attachments)
        locationsHelpers.setLocations(locationsModule, response.data.data.locations)
        locationsHelpers.setLocationTypes(locationsModule, response.data.data.location_types)
        return response
      })
  },

  /**
   * POST (create) new record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  post (context) {
    let client = context.getters['get']

    let notes = []
    let note = context.getters['get'].note || null
    if (note !== null && note.trim() !== '') {
      notes.push(note)
    }

    let contacts = contactsHelpers.contacts(contactsModule)
    let attachments = attachmentsHelpers.attachments(attachmentsModule)
    let locations = locationsHelpers.locations(locationsModule)

    return ApiService
      .post(`clients`, transformCreate(
        client, notes, contacts, attachments, locations
      ))
      .then((response) => {
        notesHelpers.purge(notesModule)
        contactsHelpers.purgeContacts(contactsModule)
        attachmentsHelpers.purge(attachmentsModule)
        locationsHelpers.purgeLocations(locationsModule)
        context.commit('set', defaults())
        utils.addRecentHistory(module, response.data.data)
        return response
      })
  },

  /**
   * Transfers client(s) to new agency and/or partner.
   *
   * @param context
   * @param clientIds
   * @param partnerId
   * @param agencyId
   * @returns {Promise}
   */
  performTransfer (context, { clientIds, partnerId, agencyId }) {
    let data = {
      client_ids: clientIds,
      partner_id: partnerId,
      agency_id: agencyId
    }

    return ApiService.post(`clients/transfer/perform`, data).then((response) => {
      utils.addRecentHistory('admin/clientsTransfer', response.data.data.transferred)
    })
  },

  /**
   * Gathers information about client being transferred.
   *
   * @param context
   * @param clientIds
   * @param partnerId
   * @param agencyId
   * @returns {Promise}
   */
  transferCheck (context, { clientIds, partnerId, agencyId }) {
    let data = {
      client_ids: clientIds,
      partner_id: partnerId,
      agency_id: agencyId
    }
    return ApiService.post(`clients/transfer/check`, data).then((response) => {
      context.commit('setTransferDetails', response.data.data)
    })
  },

  /**
   * PUT (update) existing record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  put (context) {
    let client = context.getters['get']

    return ApiService
      .put(`clients/${client.id}`, transformUpdate(client))
      .then((response) => {
        return response
      })
  },

  /**
   * DELETE existing record through the API.
   *
   * @param context Object
   *
   * @return Promise
   */
  delete (context) {
    let id = context.getters['get'].id
    return ApiService
      .delete(`clients/${id}`)
      .then((response) => {
        return response
      })
  },

  /**
   * Queries (e.g. find) multiple records from the API.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  query (context, params) {
    return ApiService
      .get(`clients`, params)
      .then((response) => {
        return response
      })
  }
}

export const mutations = {
  /**
   * Set all the data properties according to the provided data.
   *
   * @param state Object
   * @param data  Object
   */
  set (state, data) {
    state.data = {
      id: data.id,
      name: data.name,
      fin_account_no: data.fin_account_no,
      description: data.description,
      partner: data.partner,
      agency: data.agency,
      created_at: data.created_at,
      updated_at: data.updated_at,
      notes: data.notes
    }
  },

  setFormData (state, data) {
    state.form = {
      partners: data.partners
    }
  },

  /**
   * Sets the original primary data.
   *
   * @param state Object
   * @param data  Object
   */
  setOriginal (state, data) {
    state.original = {
      name: data.name,
      fin_account_no: data.fin_account_no,
      description: data.description
    }
  },

  /**
   * Sets the "modified" status to true or false
   *
   * @param state Object
   * @param isModified Boolean
   */
  setIsModified (state, isModified) {
    state.isModified = isModified
  },

  /**
   * Sets the transferDetails of all affected
   * assets of the selected client.
   *
   * @param state
   * @param data
   */
  setTransferDetails (state, data) {
    state.data.transferDetails = Object.assign([], data)
  },

  /**
   * Sets a specific property and value
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value String
   */
  setProp (state, { name, value }) {
    utils.mutateProp(state.data, name, value)
  },

  /**
   * Pushes a new element to an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value mixed  The variable to push onto the array.
   */
  pushProp (state, { name, value }) {
    state.data[name].push(value)
  },

  /**
   * Sets the values for multiple properties.
   *
   * @param state      Object
   * @param properties Array of objects in the form { name: 'propName', value: 'xyz' }
   */
  setProps (state, properties) {
    properties.forEach(({ name, value }) => {
      utils.mutateProp(state.data, name, value)
    })
  },

  /**
   * Resets the state.
   *
   * @param state Object
   */
  reset (state) {
    state.data = defaults()
    state.isModified = false
    state.original.name = ''
    state.original.fin_account_no = ''
    state.original.description = ''
    store.commit('admin/clientsModule/attachments/purge')
    store.commit('admin/clientsModule/attachments/resetFormState')
    store.commit('admin/clientsModule/contacts/purge')
    store.commit('admin/clientsModule/contacts/resetFormState')
    store.commit('admin/clientsModule/locations/purge')
    store.commit('admin/clientsModule/locations/resetFormState')
    store.commit('admin/clientsModule/notes/purge')
    store.commit('admin/clientsModule/notes/resetFormState')
  },

  /**
   * Resets the form's state.
   *
   * @param state Object
   */
  resetForm (state) {
    state.form = formDefaults()
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
  modules: {
    notes: clientNotesModule,
    contacts: clientContactsModule,
    attachments: clientAttachmentsModule,
    locations: clientLocationsModule
  }
}



// WEBPACK FOOTER //
// ./src/store/administration/clients/clientsModule.js